import {useEffect, useRef, useState} from "react";
import moment from "moment-timezone";
import {fetchDeploymentType, fetchHydrateStatus, requestHydration} from "../../utils/dashboardCalls";
import {formatData} from "../../utils/utils";

const PERTH_TZ = "Australia/Perth";
const POLL_INTERVAL_SECONDS = 5 * 60;

export const useHydration = ({ openSnackbar }) => {
    // ─── Hydration state ─────────────────────────────────────────────────────────
    const [hydrationInfo, setHydrationInfo] = useState({
        isFileLevel: false,
        state: null,           // 'hydrated' | 'hydrating' | 'dehydrated' | 'dehydrating' | null
        since: null,           // UTC ISO string
        expires: null,         // UTC ISO string
        deployedCompanyId: null,
    });
    const hasFiles = hydrationInfo.isFileLevel && hydrationInfo.state === 'hydrated';

    // ─── Hydration UI state ──────────────────────────────────────────────────────
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [customDate, setCustomDate] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);

    // ─── Hydration status fetch ───────────────────────────────────────────────────
    const refreshHydrationStatusRef = useRef(null);
    const refreshHydrationStatus = () => {
        const mockRaw = process.env.NODE_ENV === 'development' && localStorage.getItem('__NEXUS_HYDRATION_MOCK__');
        if (mockRaw) {
            try {
                const mock = JSON.parse(mockRaw);
                setHydrationInfo({
                    isFileLevel: true,
                    state: mock.state ?? 'dehydrated',
                    since: mock.since ?? null,
                    expires: mock.expires ?? null,
                    deployedCompanyId: mock.deployedCompanyId ?? 99999,
                });
            } catch {
                console.warn('Invalid __NEXUS_HYDRATION_MOCK__ value');
            }
            return;
        }

        fetchDeploymentType()
            .then(response => {
                const deployedCompanyId = response?.result?.deployed_company_id;
                const file_level_details = response?.result?.file_level_details;
                if (file_level_details !== 'true') {
                    setHydrationInfo({isFileLevel: false, state: null, since: null, expires: null, deployedCompanyId: null});
                    return;
                }
                fetchHydrateStatus()
                    .then(statusResponse => {
                        const entry = statusResponse?.result?.[deployedCompanyId];
                        if (!entry) {
                            openSnackbar("Unable to retrieve file-level data status. Please refresh.", "error");
                            return;
                        }
                        setHydrationInfo({
                            isFileLevel: true,
                            state: entry.state ?? null,
                            since: entry.since ?? null,
                            expires: entry.expires ?? null,
                            deployedCompanyId,
                        });
                    })
                    .catch(() => openSnackbar("Unable to retrieve file-level data status. Please refresh.", "error"));
            })
            .catch(() => openSnackbar("Unable to retrieve deployment information. Please refresh.", "error"));
    };
    refreshHydrationStatusRef.current = refreshHydrationStatus;

    useEffect(() => {
        refreshHydrationStatus();
    }, []); // Reverted: Load hydration status on mount regardless of admin job selection.

    // ─── Countdown target ─────────────────────────────────────────────────────────
    const [hydrationPollTarget, setHydrationPollTarget] = useState(null);

    useEffect(() => {
        const {state, expires} = hydrationInfo;
        if (state === 'hydrating') {
            setHydrationPollTarget(moment().add(POLL_INTERVAL_SECONDS, 'seconds').toISOString());
        } else if (state === 'hydrated' && expires) {
            setHydrationPollTarget(expires);
        } else {
            setHydrationPollTarget(null);
        }
    }, [hydrationInfo.state, hydrationInfo.expires]);

    // ─── Hydration computed values ────────────────────────────────────────────────
    const localTz = PERTH_TZ;
    const minLocalDate = moment.tz(localTz).add(1, 'day').format("YYYY-MM-DDTHH:mm");
    const selectedUTC = customDate
        ? formatData(customDate, 'datetime_tz', {fromTz: localTz, toTz: 'UTC', outputFormat: 'iso'})
        : null;
    const selectedDisplayLocal = customDate
        ? formatData(customDate, 'datetime_tz', {fromTz: localTz, toTz: localTz, outputFormat: 'human'})
        : null;
    const isValidDate = !!customDate && moment.tz(customDate, localTz).isAfter(moment.tz(localTz).add(1, 'day').subtract(1, 'minute'));
    const canRequestHydration = isValidDate;
    const displayInLocalTz = (utcISO) =>
        formatData(utcISO, 'datetime_tz', {fromTz: 'UTC', toTz: localTz, outputFormat: 'human'});

    // ─── Hydration request submit ─────────────────────────────────────────────────
    const handleHydrationRequested = () => {
        setConfirmOpen(false);
        fetchHydrateStatus()
            .then(statusResponse => {
                const entry = statusResponse?.result?.[hydrationInfo.deployedCompanyId];
                const freshState = entry?.state;
                if (freshState && freshState !== 'dehydrated') {
                    setHydrationInfo(prev => ({
                        ...prev,
                        state: freshState,
                        since: entry?.since ?? null,
                        expires: entry?.expires ?? null,
                    }));
                    openSnackbar(`Hydration state has changed to "${freshState}". Please review and try again.`, "warning");
                    return;
                }
                requestHydration(selectedUTC)
                    .then(() => {
                        setSelectedPreset(null);
                        setCustomDate("");
                        refreshHydrationStatus();
                    })
                    .catch(() => openSnackbar("Hydration request failed. Please try again.", "error"));
            })
            .catch(() => openSnackbar("Unable to verify current status. Please try again.", "error"));
    };

    return {
        hydrationInfo,
        hasFiles,
        refreshHydrationStatusRef,
        hydrationPollTarget,
        setHydrationPollTarget,
        confirmOpen,
        setConfirmOpen,
        selectedPreset,
        setSelectedPreset,
        customDate,
        setCustomDate,
        minLocalDate,
        canRequestHydration,
        selectedDisplayLocal,
        displayInLocalTz,
        handleHydrationRequested,
    };
};